import { useCallback, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { TextButton } from '../../../Atoms/TextButton';
import { EngagementFeatureInput, FlagNotice } from '../../../../Api/Advise/useEngagementFeatureInput';
import { AdviseFeatureResponse } from '../../../../types/advise';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { LoadError } from '../LoadError';
import { useEngagementEditor } from './EngagementEditorContext';
import { ExportRequirementSheetDialog } from './ExportRequirementSheetDialog';
import { FeatureResponses } from './FeatureResponses';
import { ImportResponseDialog } from './ImportResponseDialog';
import { SUPPLIER_GONE } from './SupplierFeatureInput.calc';
import { PanelHeader } from './PanelHeader';
import { RoundTripCard } from './RoundTripCard';

type OpenDialog = { kind: 'export' | 'import'; supplierId: string } | null;

interface SupplierFeatureInputViewProps {
  answers: EngagementFeatureInput;
  onBack: () => void;
}

/** Supplier Feature Input: each shortlisted supplier's requirement sheet, out and back, and their answers. */
export const SupplierFeatureInputView = ({ answers, onBack }: SupplierFeatureInputViewProps) => {
  const { engagement, canEdit } = useEngagementEditor();
  const { featureInput: loaded, stale, failed, reload, setFlag } = answers;
  // The shortlist or the matrix may have changed since the last load, so the view waits for a newer one.
  const featureInput = stale ? null : loaded;
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [flagNotice, setFlagNotice] = useState<FlagNotice | null>(null);
  const [supplierGone, setSupplierGone] = useState(false);

  // A flag's note is about the answers as they were, so it goes once they are reloaded or a dialog opens.
  const reloadAnswers = useCallback(() => {
    setFlagNotice(null);
    reload();
  }, [reload]);
  const openDialog = (kind: 'export' | 'import', supplierId: string) => {
    setFlagNotice(null);
    setSupplierGone(false);
    setDialog({ kind, supplierId });
  };
  const closeOnSupplierGone = () => {
    setDialog(null);
    setSupplierGone(true);
    reloadAnswers();
  };

  const header = (
    <Box className="flex items-start justify-between gap-4">
      <PanelHeader
        title="Supplier Feature Input"
        description="Export each shortlisted supplier's requirement sheet individually, then import their response here as it comes back — reviewed responses feed Supplier Scoring."
      />
      <TextButton onClick={onBack}>← Supplier Selection</TextButton>
    </Box>
  );

  if (!featureInput) {
    return (
      <Box className="flex flex-col gap-3">
        {header}
        {failed ? (
          <LoadError
            subject="the suppliers' feature input"
            consequence="there is nothing to show yet"
            onRetry={reloadAnswers}
          />
        ) : (
          <ComponentSpinner sx={{ padding: '3rem' }} />
        )}
      </Box>
    );
  }

  const { suppliers, required_categories: requiredCategories, responses } = featureInput;
  const handleFlag = async (response: AdviseFeatureResponse, flagged: boolean) =>
    setFlagNotice(await setFlag(response, flagged));

  return (
    <Box className="flex flex-col gap-4">
      {header}
      {failed && (
        <LoadError
          subject="the suppliers' feature input"
          consequence="what shows may be out of date"
          onRetry={reloadAnswers}
        />
      )}
      {supplierGone && <Alert severity="info">{SUPPLIER_GONE}</Alert>}
      {suppliers.length === 0 ? (
        <Alert severity="info">Shortlist suppliers on Supplier Selection to request their feature input.</Alert>
      ) : (
        <>
          {requiredCategories.length === 0 && (
            <Alert severity="info">
              Tick the features this engagement needs on the Functionality Matrix to build the requirement sheet.
            </Alert>
          )}
          <Box className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {suppliers.map((supplier) => (
              <RoundTripCard
                key={supplier.id}
                supplier={supplier}
                canEdit={canEdit}
                canExport={requiredCategories.length > 0}
                onExport={() => openDialog('export', supplier.id)}
                onImport={() => openDialog('import', supplier.id)}
              />
            ))}
          </Box>
          {flagNotice && <Alert severity={flagNotice.severity}>{flagNotice.message}</Alert>}
          <FeatureResponses
            categories={requiredCategories}
            suppliers={suppliers}
            responses={responses}
            onFlag={canEdit ? handleFlag : undefined}
          />
        </>
      )}

      {dialog?.kind === 'export' && (
        <ExportRequirementSheetDialog
          engagementId={engagement.id}
          engagementName={engagement.engagement_name}
          suppliers={suppliers}
          supplierId={dialog.supplierId}
          requiredCategories={requiredCategories}
          onClose={() => setDialog(null)}
          onExported={reloadAnswers}
          onShortlistChanged={closeOnSupplierGone}
        />
      )}
      {dialog?.kind === 'import' && (
        <ImportResponseDialog
          engagementId={engagement.id}
          engagementName={engagement.engagement_name}
          suppliers={suppliers}
          supplierId={dialog.supplierId}
          onClose={() => setDialog(null)}
          onImported={reloadAnswers}
          onShortlistChanged={reloadAnswers}
        />
      )}
    </Box>
  );
};
